import { useEffect } from 'react';
import styled from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useRadioMetadata } from '../../../../helpers';
import { usePlayer } from '../../../player';
import type { TrackInfoContentProps } from '../TrackInfoContent';
import { Caption } from './Caption';
import { Title } from './Title';

export function RadioTrackInfo({
  track,
  titleTextStyle,
  authorTextStyle,
  wrapTitle,
  isVisible,
  disableTicker
}: TrackInfoContentProps) {
  const { currentItem } = usePlayer();

  const derivedTrack = track ?? currentItem;
  const { id, metadataSettings, link, hasCaption } = derivedTrack ?? {};

  const { fetcherType, shouldUseCORSProxy, collectionStatus } =
    metadataSettings ?? {};

  const {
    data: caption,
    isLoading: isLoadingCaptionData,
    isFetched
  } = useRadioMetadata({
    id,
    fetcherType,
    forceCORSProxy: shouldUseCORSProxy,
    enabled: hasCaption && isVisible,
    url: link,
    // Если используем currentItem, значит Caption стоит в плеере. Если используем track, значит Caption стоит в плейлисте
    withShortRevalidation: !track || track === currentItem
  });

  const isCollectingFetcherType = collectionStatus === 'pending';
  const isLoadingCaption =
    !!hasCaption &&
    (isLoadingCaptionData || isCollectingFetcherType || !isFetched);
  const hasCaptionValue = !!hasCaption && !!caption;
  const withCaption = isLoadingCaption || hasCaptionValue;

  useEffect(() => {
    if (track || !navigator.mediaSession?.metadata || !caption) {
      return;
    }

    navigator.mediaSession.metadata.artist = caption;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [track, caption, navigator.mediaSession?.metadata]);

  return (
    <Container>
      <Title track={track} textStyle={titleTextStyle} wrap={wrapTitle} />

      {withCaption && (
        <Caption
          textStyle={authorTextStyle}
          caption={caption}
          isLoading={isLoadingCaption}
          disableTicker={disableTicker}
        />
      )}
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'radio-track-info',
    element: 'container'
  })
})`
  width: fit-content;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
